import React, { useState } from "react";
import { createPortal } from "react-dom";
import { GripVertical, Search, Star, X } from "lucide-react";
import { C, FONT } from "../styles";
import { ACTIONS_CATALOG, MAX_FAVOURITES } from "../constants";
import type { ActionId } from "../types";

interface CreateActionsPanelProps {
  initialFavs: ActionId[];
  onSave: (newFavs: ActionId[]) => void;
  onClose: () => void;
}

export default function CreateActionsPanel({
  initialFavs,
  onSave,
  onClose,
}: CreateActionsPanelProps) {
  const [favs, setFavs] = useState<ActionId[]>(initialFavs);
  const [query, setQuery] = useState("");
  const [dragId, setDragId] = useState<ActionId | null>(null);

  const toggleFav = (id: ActionId) => {
    if (favs.includes(id)) {
      setFavs(favs.filter((f) => f !== id));
    } else {
      if (favs.length >= MAX_FAVOURITES) return;
      setFavs([...favs, id]);
    }
  };

  const reorder = (fromId: ActionId, toId: ActionId) => {
    if (fromId === toId) return;
    const newFavs = [...favs];
    const fromIdx = newFavs.indexOf(fromId);
    const toIdx = newFavs.indexOf(toId);
    newFavs.splice(fromIdx, 1);
    newFavs.splice(toIdx, 0, fromId);
    setFavs(newFavs);
  };

  const filtered = ACTIONS_CATALOG.filter((a) =>
    a.label.toLowerCase().includes(query.toLowerCase())
  );
  const favActions = favs
    .map((id) => ACTIONS_CATALOG.find((a) => a.id === id))
    .filter(Boolean) as typeof ACTIONS_CATALOG[number][];
  const atMax = favs.length >= MAX_FAVOURITES;

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(16,26,43,0.42)",
          zIndex: 1000,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          maxHeight: "100vh",
          width: "min(440px, 96vw)",
          background: "#fff",
          boxShadow: "-12px 0 40px rgba(16,26,43,0.18)",
          display: "flex",
          flexDirection: "column",
          zIndex: 1001,
          fontFamily: FONT,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            borderBottom: "1px solid " + C.line,
          }}
        >
          <h3 style={{ fontSize: 17, fontWeight: 600, color: C.ink }}>
            Create actions
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: C.muted,
              padding: 4,
              display: "inline-flex",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
            maxHeight: "calc(100vh - 140px)",
            padding: "18px 22px 8px",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div style={{ position: "relative", marginBottom: 14 }}>
            <Search
              size={16}
              style={{
                position: "absolute",
                left: 13,
                top: "50%",
                transform: "translateY(-50%)",
                color: C.faint,
              }}
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search all create actions"
              style={{
                width: "100%",
                border: "1px solid " + C.line,
                borderRadius: 11,
                padding: "11px 38px 11px 40px",
                fontFamily: FONT,
                fontSize: 14,
                color: C.ink,
                outline: "none",
              }}
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: C.faint,
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div
            style={{
              fontSize: 12.5,
              color: C.muted,
              lineHeight: 1.5,
              marginBottom: 8,
            }}
          >
            Choose your favourites. These appear on your Create actions row. If
            you do not customise, your top actions update as Novala learns what
            you use.
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: C.ink,
              marginBottom: 14,
            }}
          >
            Select up to {MAX_FAVOURITES}{" "}
            <span style={{ color: C.faint, fontWeight: 500 }}>
              ({favs.length} selected)
            </span>
          </div>

          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: C.faint,
              margin: "18px 0 6px",
            }}
          >
            Favourites
          </div>
          {favActions.length === 0 ? (
            <div style={{ fontSize: 13, color: C.muted, padding: "14px 8px" }}>
              No favourites yet. Star actions below to add them.
            </div>
          ) : (
            favActions.map((a) => (
              <div
                key={a.id}
                draggable
                onDragStart={() => setDragId(a.id)}
                onDragEnd={() => setDragId(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragId) reorder(dragId, a.id);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "11px 8px",
                  borderRadius: 10,
                }}
              >
                <span
                  style={{
                    color: C.faint,
                    cursor: "grab",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <GripVertical size={14} />
                </span>
                <button
                  onClick={() => toggleFav(a.id)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: C.teal,
                    padding: 0,
                    display: "inline-flex",
                  }}
                >
                  <Star size={20} fill="currentColor" />
                </button>
                <span
                  style={{
                    flex: 1,
                    fontSize: 14,
                    color: C.ink,
                    fontWeight: 500,
                  }}
                >
                  {a.label}
                </span>
              </div>
            ))
          )}

          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: C.faint,
              margin: "18px 0 6px",
            }}
          >
            Payroll
          </div>
          {filtered.length === 0 ? (
            <div style={{ fontSize: 13, color: C.muted, padding: "14px 8px" }}>
              No actions match your search.
            </div>
          ) : (
            filtered.map((a) => {
              const isFav = favs.includes(a.id);
              const disabled = !isFav && atMax;
              return (
                <div
                  key={a.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "11px 8px",
                    borderRadius: 10,
                  }}
                >
                  <button
                    onClick={() => !disabled && toggleFav(a.id)}
                    disabled={disabled}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: disabled ? "not-allowed" : "pointer",
                      color: isFav ? C.teal : C.faint,
                      padding: 0,
                      display: "inline-flex",
                      opacity: disabled ? 0.35 : 1,
                    }}
                  >
                    <Star size={20} fill={isFav ? "currentColor" : "none"} />
                  </button>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 14,
                      color: C.ink,
                      fontWeight: 500,
                    }}
                  >
                    {a.label}
                  </span>
                </div>
              );
            })
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 22px",
            borderTop: "1px solid " + C.line,
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: C.muted,
              fontWeight: 600,
              padding: "8px 4px",
              cursor: "pointer",
              fontFamily: FONT,
              fontSize: 14,
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(favs)}
            style={{
              background: C.teal,
              color: "#fff",
              border: "none",
              borderRadius: 11,
              padding: "10px 22px",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              fontFamily: FONT,
              boxShadow: "0 2px 8px rgba(21,160,140,0.28)",
            }}
          >
            Save
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}